import {
  computed,
  ref,
  reactive,
  watch,
  nextTick,
} from 'vue';
import { useI18n } from 'vue-i18n';
import { normalizeBaseUrl } from '@/helpers/tenantUrlHelper';
import { completeCheckout } from '@/services/checkoutService';
import CartService from '@/services/cartService';
import { BOOKING_ORIGIN } from '@/constants/bookingOrigin';
import {
  PAYMENT_METHOD_PAYPAL,
  PAYMENT_METHOD_NETPAY,
  PAYMENT_METHOD_STRIPE,
  PAYMENT_METHOD_AUTHORIZENET,
  PAYMENT_METHOD_MERCADOPAGO,
} from '@/types/checkout';
import type {
  PaymentMethod,
  MercadoPagoConfig,
  StripeConfig,
  StripeInstance,
  StripeElements,
  StripePaymentElement,
  StripeConstructor,
  NetPaySdk,
  NetPayTokenResponse,
  NetPayTokenResult,
  PaymentMethodExtras,
  PaymentFields,
  RedsysPayload,
  WindowWithPaymentSdk,
} from '@/types/checkout';
import type { useCheckoutData } from './useCheckoutData';

export function useCheckoutPayment(data: ReturnType<typeof useCheckoutData>) {
  const { t } = useI18n();

  const paymentLoading = ref<boolean>(false);
  const paymentError = ref<string | null>(null);
  const paymentSuccess = ref<string | null>(null);
  const selectedPaymentMethodId = ref<number | null>(null);
  const savePaymentMethod = ref<boolean>(false);
  const mercadoPagoConfig = ref<MercadoPagoConfig | null>(null);
  const netPayTokenInfo = ref<Record<string, unknown> | null>(null);
  const netPayTokenId = ref<string | null>(null);
  const netPayScriptPromise = ref<Promise<NetPaySdk> | null>(null);
  const stripeConfig = ref<StripeConfig | null>(null);
  const stripeInstance = ref<StripeInstance | null>(null);
  const stripeElements = ref<StripeElements | null>(null);
  const stripePaymentElement = ref<StripePaymentElement | null>(null);
  const stripeScriptPromise = ref<Promise<StripeConstructor> | null>(null);
  const acceptLegal = ref<boolean>(false);
  const acceptMedical = ref<boolean>(false);
  const acceptMarketing = ref<boolean>(false);
  const legalError = ref<string | null>(null);

  const cardForm = reactive({
    number: '',
    expiry: '',
    cvv: '',
    holder: '',
  });

  const paymentMethods = computed<PaymentMethod[]>(() => {
    const methods = data.checkoutInfo.value?.data?.paymentMethods;
    if (Array.isArray(methods)) {
      const payPalId = data.checkoutInfo.value?.data?.payPalId;
      return methods.filter((method): method is PaymentMethod => {
        if (!method || typeof method !== 'object') {
          return false;
        }
        const type = Number(method.type ?? 0);
        if (type === PAYMENT_METHOD_PAYPAL && !payPalId) {
          return false;
        }
        return true;
      });
    }
    return [];
  });

  const totalPending = computed(() => data.sales.value
    .reduce((acc: number, sale) => acc + (Number(sale.pending) || 0), 0));

  const totalDue = computed(() => {
    const raw = totalPending.value - (Number(data.promoDiscount.value) || 0);
    return raw > 0 ? raw : 0;
  });

  const hasDeposits = computed(() => Object.keys(data.saleDeposits.value).length > 0);

  const hasActivePromoCodes = computed(() => Boolean(data.checkoutInfo.value?.data?.hasActivePromoCodes));

  // amount actually charged today: each sale pays its deposit if it has one, the full pending otherwise
  const totalDueToday = computed(() => {
    if (!hasDeposits.value) {
      return totalDue.value;
    }
    return data.sales.value.reduce((acc: number, sale) => {
      const pending = Number(sale.pending) || 0;
      const deposit = sale?.id !== undefined && sale?.id !== null
        ? data.saleDeposits.value[String(sale.id)]
        : undefined;
      return acc + (deposit !== undefined ? Math.min(deposit, pending) : pending);
    }, 0);
  });

  const totalRemainingAtCenter = computed(() => Math.max(totalDue.value - totalDueToday.value, 0));

  const paymentIconPaths = {
    card: 'M3 7h18a1 1 0 011 1v8a2 2 0 01-2 2H4a2 2 0 01-2-2V8a1 1 0 011-1zm0 4h20',
    wallet: 'M4 6h14a2 2 0 012 2v2h-4a2 2 0 00-2 2v2a2 2 0 002 2h4v2a2 2 0 01-2 2H4a2 2 0 01-2-2V8a2 2 0 012-2zm12 8h4v-4h-4z',
    shield: 'M12 3l7 3v5c0 4.418-3.134 8.268-7 9-3.866-.732-7-4.582-7-9V6l7-3z',
  };

  const getPaymentIconPath = (method: PaymentMethod | null) => {
    if (!method) {
      return paymentIconPaths.card;
    }
    const type = Number(method.type ?? 0);
    if (type === PAYMENT_METHOD_MERCADOPAGO) {
      return paymentIconPaths.wallet;
    }
    if (type === PAYMENT_METHOD_NETPAY) {
      return paymentIconPaths.shield;
    }
    return paymentIconPaths.card;
  };

  const selectedPaymentMethod = computed(() => paymentMethods.value
    .find((method: PaymentMethod) => method?.id === selectedPaymentMethodId.value) ?? null);

  const requiresNetPay = computed(() => Number(selectedPaymentMethod.value?.type ?? 0) === PAYMENT_METHOD_NETPAY);
  const requiresAuthorizeNet = computed(() => Number(selectedPaymentMethod.value?.type ?? 0) === PAYMENT_METHOD_AUTHORIZENET);
  const requiresCardForm = computed(() => requiresNetPay.value || requiresAuthorizeNet.value);
  const isStripeMethod = computed(() => {
    const type = Number(selectedPaymentMethod.value?.type ?? 0);
    if (type === PAYMENT_METHOD_STRIPE) {
      return true;
    }
    const name = (selectedPaymentMethod.value?.name || '').toLowerCase();
    return name.includes('stripe');
  });
  const isMercadoPago = computed(() => Number(selectedPaymentMethod.value?.type ?? 0) === PAYMENT_METHOD_MERCADOPAGO);

  const getNetPayConfig = () => data.checkoutInfo.value?.data ?? {};

  const netPayAvailable = computed(() => {
    if (!requiresNetPay.value) {
      return true;
    }
    const config = getNetPayConfig();
    return Boolean(config.netPayPublicKey);
  });

  const sanitizeCardNumber = (value: string | null) => (value ? String(value).replace(/\D/g, '') : '');

  const sanitizeCvv = (value: string | null) => (value ? String(value).replace(/\D/g, '') : '');

  const parseExpiry = (rawValue: string | null) => {
    if (!rawValue || typeof rawValue !== 'string') {
      return { month: null, year: null };
    }
    const sanitized = rawValue.replace(/\s+/g, '');
    const parts = sanitized.split('/');
    if (parts.length !== 2) {
      return { month: null, year: null };
    }
    const [rawMonth, rawYear] = parts;
    const month = rawMonth.padStart(2, '0');
    if (!/^\d{2}$/.test(month) || Number(month) < 1 || Number(month) > 12) {
      return { month: null, year: null };
    }
    const year = rawYear.length === 2 ? rawYear : rawYear.slice(-2);
    if (!/^\d{2}$/.test(year)) {
      return { month: null, year: null };
    }
    return { month, year };
  };

  const isCardFormComplete = () => {
    if (!requiresCardForm.value) {
      return true;
    }
    const number = sanitizeCardNumber(cardForm.number);
    const cvv = sanitizeCvv(cardForm.cvv);
    const { month, year } = parseExpiry(cardForm.expiry);
    return number.length >= 12 && cvv.length >= 3 && Boolean(month && year && cardForm.holder.trim().length);
  };

  const canConfirmPayment = computed(() => data.saleIds.value.length > 0
    && selectedPaymentMethodId.value !== null
    && selectedPaymentMethodId.value !== undefined
    && acceptLegal.value
    && acceptMedical.value
    && (!requiresCardForm.value || isCardFormComplete())
    && (!requiresNetPay.value || netPayAvailable.value));

  const selectPaymentMethod = (methodId: number | string | null) => {
    if (methodId === undefined || methodId === null) {
      selectedPaymentMethodId.value = null;
      mercadoPagoConfig.value = null;
      savePaymentMethod.value = false;
      return;
    }
    const numericId = Number(methodId);
    selectedPaymentMethodId.value = numericId;
    mercadoPagoConfig.value = null;
    const method = paymentMethods.value.find((entry) => Number(entry?.id ?? 0) === numericId) || null;
    savePaymentMethod.value = Boolean(method && method.forceSaveCreditCard);
  };

  const destroyStripeElement = () => {
    if (stripePaymentElement.value && typeof stripePaymentElement.value.destroy === 'function') {
      stripePaymentElement.value.destroy();
    }
    stripePaymentElement.value = null;
    stripeElements.value = null;
  };

  const loadNetPaySdk = (): Promise<NetPaySdk> => {
    if (typeof window === 'undefined') {
      return Promise.reject(new Error('NetPay SDK is not available in this context.'));
    }
    const win = window as WindowWithPaymentSdk;
    if (win.NetPay) {
      return Promise.resolve(win.NetPay);
    }
    if (!netPayScriptPromise.value) {
      netPayScriptPromise.value = new Promise<NetPaySdk>((resolve, reject) => {
        const script = document.createElement('script');
        script.src = 'https://docs.netpay.mx/cdn/v1.3/netpay.min.js';
        script.async = true;
        script.onload = () => {
          if (win.NetPay) {
            resolve(win.NetPay);
          } else {
            reject(new Error('Unable to load NetPay SDK.'));
          }
        };
        script.onerror = () => reject(new Error('Unable to load NetPay SDK.'));
        document.head.appendChild(script);
      });
    }
    return netPayScriptPromise.value;
  };

  const loadStripeSdk = (): Promise<StripeConstructor> => {
    if (typeof window === 'undefined') {
      return Promise.reject(new Error(t('Unable to load the payment processor. Please try again later.')));
    }
    const win = window as WindowWithPaymentSdk;
    if (win.Stripe) {
      return Promise.resolve(win.Stripe);
    }
    if (!stripeScriptPromise.value) {
      stripeScriptPromise.value = new Promise<StripeConstructor>((resolve, reject) => {
        const script = document.createElement('script');
        script.src = 'https://js.stripe.com/v3/';
        script.async = true;
        script.onload = () => {
          if (win.Stripe) {
            resolve(win.Stripe);
          } else {
            reject(new Error(t('Unable to load the payment processor. Please try again later.')));
          }
        };
        script.onerror = () => reject(new Error(t('Unable to load the payment processor. Please try again later.')));
        document.head.appendChild(script);
      });
    }
    return stripeScriptPromise.value;
  };

  const mountStripeElement = async () => {
    if (!stripeConfig.value) {
      destroyStripeElement();
      return;
    }
    destroyStripeElement();
    const StripeLib = await loadStripeSdk();
    if (!StripeLib) {
      throw new Error(t('Unable to load the payment processor. Please try again later.'));
    }
    stripeInstance.value = StripeLib(stripeConfig.value.publicKey);
    const appearance = { theme: 'stripe' };
    stripeElements.value = stripeInstance.value.elements({
      appearance,
      clientSecret: stripeConfig.value.clientSecret,
    });
    stripePaymentElement.value = stripeElements.value.create('payment', { layout: 'tabs' });
    await nextTick();
    const target = document.getElementById('stripe-payment-element');
    if (target) {
      target.innerHTML = '';
      stripePaymentElement.value.mount('#stripe-payment-element');
    }
  };

  const ensureStripeReady = async () => {
    if (!stripeConfig.value) {
      return false;
    }
    if (!stripeInstance.value || !stripeElements.value || !stripePaymentElement.value) {
      await mountStripeElement();
    }
    return Boolean(stripeInstance.value && stripeElements.value);
  };

  const tokenizeNetPayCard = async (): Promise<NetPayTokenResult> => {
    const config = getNetPayConfig();
    const publicKey = config.netPayPublicKey;
    if (!publicKey) {
      throw new Error(t('This payment method is temporarily unavailable. Please try a different one.'));
    }
    const number = sanitizeCardNumber(cardForm.number);
    const cvv = sanitizeCvv(cardForm.cvv);
    const { month, year } = parseExpiry(cardForm.expiry);
    if (!number || !cvv || !month || !year) {
      throw new Error(t('Please complete the card information before continuing.'));
    }

    const NetPay = await loadNetPaySdk();
    if (!NetPay || typeof NetPay.setApiKey !== 'function') {
      throw new Error(t('Unable to load the payment processor. Please try again later.'));
    }

    NetPay.setApiKey(publicKey);
    NetPay.setSandboxMode(config.netPayProdMode === false);

    const cardPayload = {
      cardNumber: number,
      expMonth: month,
      expYear: year,
      cvv2: cvv,
      cardHolderName: cardForm.holder,
    };

    const tokenResult = await new Promise<NetPayTokenResult>((resolve, reject) => {
      NetPay.token.create(
        cardPayload,
        (response: NetPayTokenResponse) => {
          try {
            const rawPayload = response?.message?.data ?? response;
            const parsed = typeof rawPayload === 'string' ? JSON.parse(rawPayload) : rawPayload;
            const info = parsed && typeof parsed === 'object' ? (parsed as Record<string, unknown>) : null;
            if (info) {
              info.ccv = cardPayload.cvv2;
            }
            const tokenIdRaw = info && ('tokenId' in info ? info.tokenId : info.id);
            const tokenId = tokenIdRaw !== null && tokenIdRaw !== undefined ? String(tokenIdRaw) : null;
            netPayTokenInfo.value = info;
            netPayTokenId.value = tokenId;
            resolve({
              tokenId,
              info,
              notPersisted: {
                card: cardForm.number,
                exp: cardForm.expiry,
              },
            });
          } catch (error) {
            reject(error);
          }
        },
        (error: { message?: string | null } | null) => {
          const message = error?.message || t('Unable to process the card. Please verify the details.');
          reject(new Error(message));
        }
      );
    });

    return tokenResult;
  };

  const buildStripeConfig = (payload: unknown): StripeConfig | null => {
    if (!payload) {
      return null;
    }
    const payloadRecord = typeof payload === 'object' && payload !== null
      ? (payload as Record<string, unknown>)
      : null;
    const clientSecret = typeof payload === 'string'
      ? payload
      : (payloadRecord?.clientSecret ?? payloadRecord?.client_secret ?? null);
    const publicKey = payloadRecord?.publicKey ?? payloadRecord?.pk ?? null;
    if (typeof clientSecret !== 'string' || typeof publicKey !== 'string') {
      throw new Error(t('Unable to load the payment processor. Please try again later.'));
    }
    return { clientSecret, publicKey };
  };

  const submitRedsysForm = (redsysData: unknown) => {
    const redsysPayload = redsysData && typeof redsysData === 'object'
      ? (redsysData as RedsysPayload)
      : null;
    if (!redsysPayload?.url || !redsysPayload?.data) {
      throw new Error(t('Unable to load the payment processor. Please try again later.'));
    }
    const form = document.createElement('form');
    form.method = 'POST';
    form.action = redsysPayload.url;
    form.style.display = 'none';

    const appendInput = (name: string, value: unknown) => {
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = name;
      input.value = value !== null && value !== undefined ? String(value) : '';
      form.appendChild(input);
    };

    appendInput('Ds_SignatureVersion', redsysPayload.data?.ds_SignatureVersion ?? redsysPayload.data?.Ds_SignatureVersion);
    appendInput('Ds_MerchantParameters', redsysPayload.data?.ds_MerchantParameters ?? redsysPayload.data?.Ds_MerchantParameters);
    appendInput('Ds_Signature', redsysPayload.data?.ds_Signature ?? redsysPayload.data?.Ds_Signature);

    document.body.appendChild(form);
    form.submit();
  };

  const handleStripeConfirm = async () => {
    const ready = await ensureStripeReady();
    if (!ready) {
      paymentError.value = t('Unable to load the payment processor. Please try again later.');
      return;
    }
    paymentLoading.value = true;
    paymentError.value = null;
    try {
      const stripeInstanceValue = stripeInstance.value;
      const stripeElementsValue = stripeElements.value;
      if (!stripeInstanceValue || !stripeElementsValue) {
        throw new Error(t('Unable to load the payment processor. Please try again later.'));
      }
      const { error, paymentIntent } = await stripeInstanceValue.confirmPayment({
        elements: stripeElementsValue,
        confirmParams: {
          return_url: `${window.location.origin}/stripe-callback`,
        },
      });
      if (error) {
        paymentError.value = error.message || t('Unable to complete payment');
        paymentLoading.value = false;
        return;
      }
      const intentId = paymentIntent?.id
        || (stripeConfig.value?.clientSecret
          ? stripeConfig.value.clientSecret.split('_secret')[0]
          : null);
      const clientSecret = paymentIntent?.client_secret || stripeConfig.value?.clientSecret || null;
      if (intentId) {
        const searchParams = new URLSearchParams();
        searchParams.set('payment_intent', intentId);
        if (clientSecret) {
          searchParams.set('payment_intent_client_secret', clientSecret);
        }
        window.location.href = `${window.location.origin}/stripe-callback?${searchParams.toString()}`;
        return;
      }
      paymentLoading.value = false;
      paymentError.value = t('Unable to complete payment');
    } catch (error: unknown) {
      paymentError.value = data.resolveErrorMessage(error) ?? t('Unable to complete payment');
      paymentLoading.value = false;
    }
  };

  const handleConfirmPayment = async () => {
    if (!data.saleIds.value.length) {
      paymentError.value = t('No pending reservations');
      return;
    }
    if (!acceptLegal.value || !acceptMedical.value) {
      legalError.value = t('Please accept the legal terms to continue.');
      return;
    }
    if (!canConfirmPayment.value) {
      paymentError.value = t('Please select a payment method to continue.');
      return;
    }
    if (stripeConfig.value && stripeInstance.value && stripeElements.value) {
      await handleStripeConfirm();
      return;
    }
    legalError.value = null;
    mercadoPagoConfig.value = null;
    paymentLoading.value = true;
    paymentError.value = null;
    paymentSuccess.value = null;
    try {
      let methodExtras: PaymentMethodExtras = {
        tokenId: null,
        tpvOnlineInfoTemp: null,
        tpvOnlineNotPersitedInfo: null,
      };

      if (requiresNetPay.value) {
        const tokenResult = await tokenizeNetPayCard();
        methodExtras = {
          tokenId: tokenResult?.tokenId ?? null,
          tpvOnlineInfoTemp: tokenResult?.info ? JSON.stringify(tokenResult.info) : null,
          tpvOnlineNotPersitedInfo: tokenResult?.notPersisted
            ? JSON.stringify(tokenResult.notPersisted)
            : null,
        };
      } else if (requiresAuthorizeNet.value) {
        const number = sanitizeCardNumber(cardForm.number);
        const { month, year } = parseExpiry(cardForm.expiry);
        if (!number || !month || !year) {
          throw new Error(t('Please complete the card information before continuing.'));
        }
        methodExtras = {
          tokenId: null,
          tpvOnlineInfoTemp: null,
          tpvOnlineNotPersitedInfo: JSON.stringify({
            card: number,
            exp: `${month}/${year}`,
          }),
        };
      }

      const paymentFields: PaymentFields = {
        acceptConditions: acceptLegal.value,
        medicalConsent: acceptMedical.value,
        marketingOptIn: acceptMarketing.value,
      };
      if (selectedPaymentMethod.value?.allowTokenization || selectedPaymentMethod.value?.forceSaveCreditCard) {
        paymentFields.saveCard = savePaymentMethod.value;
      }

      const response = await completeCheckout({
        tenantUrl: data.tenantUrl.value,
        saleIds: data.saleIds.value,
        saleItems: [],
        paymentMethodId: selectedPaymentMethodId.value,
        voucherId: null,
        customerId: data.checkoutContext.value?.sale?.customerId ?? null,
        customer: data.buildCustomerPayload(),
        tokenId: methodExtras.tokenId,
        tpvOnlineInfoTemp: methodExtras.tpvOnlineInfoTemp,
        tpvOnlineNotPersitedInfo: methodExtras.tpvOnlineNotPersitedInfo,
        paymentFields,
        promoCode: data.promoApplied.value ? data.promoCode.value : null,
        origin: BOOKING_ORIGIN.MARKETPLACE,
      });

      if (!response) {
        paymentError.value = t('Empty checkout response');
        data.showGlobalError(paymentError.value);
        return;
      }
      if (response.ok === false) {
        const backendError = response.errorMsg || response.model?.errorMsg || null;
        paymentError.value = backendError || t('Unable to complete the request');
        data.showGlobalError(paymentError.value);
        return;
      }

      const payload = response.model ?? null;
      const mercadoPagoData = payload?.mercadoPago ?? payload?.MercadoPago ?? null;
      if (mercadoPagoData) {
        const fallbackAmount = data.currentSale.value
          ? Number(data.currentSale.value.pending ?? data.currentSale.value.totalWithDiscount ?? 0) || 0
          : 0;
        const resolvedAmount = Number(mercadoPagoData.amount ?? fallbackAmount) || 0;
        const resolvedCurrency = mercadoPagoData.currency
          ?? data.checkoutInfo.value?.data?.currency
          ?? 'EUR';
        const resolvedEmail = mercadoPagoData.email
          ?? data.checkoutInfo.value?.data?.email
          ?? '';
        const resolvedPublicKey = mercadoPagoData.pk ?? mercadoPagoData.publicKey ?? '';
        const requestId = Number(mercadoPagoData.id ?? mercadoPagoData.requestId ?? 0);
        const normalizedTenantUrl = normalizeBaseUrl(data.tenantUrl.value);
        const endpoint = normalizedTenantUrl
          ? `${normalizedTenantUrl}/api/publicmercadopago/process_payment`
          : null;
        if (!requestId || !resolvedPublicKey) {
          paymentError.value = t('Unable to load the payment processor. Please try again later.');
          return;
        }
        mercadoPagoConfig.value = {
          requestId,
          amount: resolvedAmount,
          currency: resolvedCurrency,
          email: resolvedEmail,
          publicKey: resolvedPublicKey,
          endpoint,
          payer: {
            firstName: data.checkoutInfo.value?.data?.name || '',
            lastName: data.checkoutInfo.value?.data?.surname || '',
          },
        };
        return;
      }

      const stripePayload = payload?.stripe ?? payload?.Stripe ?? null;
      if (stripePayload) {
        stripeConfig.value = buildStripeConfig({
          ...stripePayload,
          clientSecret: typeof stripePayload === 'string'
            ? stripePayload
            : (stripePayload.clientSecret ?? stripePayload.client_secret),
          publicKey: payload?.publicKey ?? stripePayload.publicKey ?? stripePayload.pk,
        });
        await nextTick();
        await mountStripeElement();
        paymentLoading.value = false;
        paymentSuccess.value = null;
        return;
      }

      const redsysPayload = payload?.redsysData ?? payload?.RedsysData ?? null;
      if (redsysPayload) {
        submitRedsysForm(redsysPayload);
        return;
      }

      if (payload?.saleNumber) {
        CartService.addSale({
          ...payload,
          tenantUrl: data.tenantUrl.value,
        });
        paymentSuccess.value = t('Partial payment applied successfully.');
        await data.loadCheckoutInfo();
        return;
      }

      paymentSuccess.value = t('Reservation confirmed successfully.');
      await data.clearCartAndRedirect(() => {
        mercadoPagoConfig.value = null;
        stripeConfig.value = null;
        destroyStripeElement();
      });
    } catch (error: unknown) {
      const backendError = data.resolveBackendErrorMessage(error) ?? data.resolveErrorMessage(error);
      paymentError.value = backendError || t('Unable to complete payment');
      data.showGlobalError(paymentError.value);
    } finally {
      paymentLoading.value = false;
    }
  };

  const handleMercadoPagoSuccess = async () => {
    paymentSuccess.value = t('Reservation confirmed successfully.');
    mercadoPagoConfig.value = null;
    await data.clearCartAndRedirect(() => {
      mercadoPagoConfig.value = null;
      stripeConfig.value = null;
      destroyStripeElement();
    });
  };

  const handleMercadoPagoError = (message: string | Record<string, unknown> | null) => {
    if (typeof message === 'string') {
      paymentError.value = message;
      return;
    }
    if (message && typeof message === 'object' && 'errorMsg' in message) {
      const errorMsg = (message as { errorMsg?: unknown }).errorMsg;
      paymentError.value = typeof errorMsg === 'string' ? errorMsg : t('Unable to complete payment');
      return;
    }
    paymentError.value = t('Unable to complete payment');
  };

  const handleMercadoPagoLoading = (state: boolean) => {
    paymentLoading.value = state;
    if (state) {
      paymentError.value = null;
    }
  };

  // Watch: auto-select first payment method when list changes
  watch(paymentMethods, (newMethods: PaymentMethod[]) => {
    if (!newMethods.length) {
      selectPaymentMethod(null);
      return;
    }
    const exists = newMethods.some((method) => method.id === selectedPaymentMethodId.value);
    if (!exists) {
      selectPaymentMethod(newMethods[0]?.id ?? null);
    }
  }, { immediate: true });

  // Watch: reset payment state when saleIds change
  watch(data.saleIds, async (newIds, oldIds) => {
    const newKey = JSON.stringify(newIds || []);
    const oldKey = JSON.stringify(oldIds || []);
    if (newKey !== oldKey && newIds.length) {
      mercadoPagoConfig.value = null;
      stripeConfig.value = null;
      destroyStripeElement();
    }
    if (!newIds.length) {
      mercadoPagoConfig.value = null;
      stripeConfig.value = null;
      destroyStripeElement();
      acceptLegal.value = false;
      acceptMarketing.value = false;
    }
  });

  // Watch: reset token/stripe/mp state when payment method changes
  watch(() => selectedPaymentMethodId.value, () => {
    netPayTokenInfo.value = null;
    netPayTokenId.value = null;
    mercadoPagoConfig.value = null;
    stripeConfig.value = null;
    destroyStripeElement();
    const method = paymentMethods.value.find((entry) => Number(entry?.id ?? 0) === selectedPaymentMethodId.value) || null;
    savePaymentMethod.value = Boolean(method && method.forceSaveCreditCard);
    data.promoError.value = null;
  });

  // Watch: reset token when card form fields change
  watch(
    () => [cardForm.number, cardForm.expiry, cardForm.cvv, cardForm.holder],
    () => {
      netPayTokenInfo.value = null;
      netPayTokenId.value = null;
    }
  );

  // Watch: update savePaymentMethod flag when selected method changes
  watch(selectedPaymentMethod, (method: PaymentMethod | null) => {
    if (!method?.allowTokenization && !method?.forceSaveCreditCard && savePaymentMethod.value) {
      savePaymentMethod.value = false;
    }
  });

  // Watch: clear legal error when legal accepted
  watch(() => acceptLegal.value, () => {
    if (acceptLegal.value) {
      legalError.value = null;
    }
  });

  return {
    paymentLoading,
    paymentError,
    paymentSuccess,
    selectedPaymentMethodId,
    selectedPaymentMethod,
    savePaymentMethod,
    mercadoPagoConfig,
    netPayTokenInfo,
    netPayTokenId,
    stripeConfig,
    stripeInstance,
    stripeElements,
    stripePaymentElement,
    cardForm,
    acceptLegal,
    acceptMedical,
    acceptMarketing,
    legalError,
    paymentMethods,
    totalPending,
    totalDue,
    hasDeposits,
    hasActivePromoCodes,
    totalDueToday,
    totalRemainingAtCenter,
    requiresNetPay,
    requiresAuthorizeNet,
    requiresCardForm,
    isStripeMethod,
    isMercadoPago,
    netPayAvailable,
    canConfirmPayment,
    selectPaymentMethod,
    isCardFormComplete,
    sanitizeCardNumber,
    sanitizeCvv,
    parseExpiry,
    getPaymentIconPath,
    handleConfirmPayment,
    handleMercadoPagoSuccess,
    handleMercadoPagoError,
    handleMercadoPagoLoading,
    destroyStripeElement,
    mountStripeElement,
    ensureStripeReady,
  };
}
